/**
 * Side-by-side comparison table for 2-3 companies.
 *
 * AC-005: Parallel columns with fixed left label column
 * AC-006: All metric rows with appropriate formatting
 * AC-007: Rank highlighting (green for best, amber for worst)
 * AC-008: Confidence badges
 * AC-009: Hidden rows when all values are null
 */

import type { CompanyComparisonColumn, CompanyComparisonMetric } from '../../types';

interface ComparisonTableProps {
  columns: CompanyComparisonColumn[];
  availableMetrics: string[];
}

// Human-readable labels for metric keys
const METRIC_LABELS: Record<string, string> = {
  company_type: 'Company Type',
  headquarters: 'Headquarters',
  founded_year: 'Founded',
  revenue: 'Revenue',
  revenue_growth: 'Revenue Growth',
  net_income: 'Net Income',
  market_cap: 'Market Cap',
  employee_count: 'Employees',
  product_count: 'Products',
  notable_customers: 'Customers (Notable)',
  kb_completeness: 'KB Completeness',
  competitive_position: 'Competitive Position',
  target_markets: 'Target Markets',
  key_differentiators: 'Key Differentiators',
  win_reasons: 'Win Reasons',
  loss_reasons: 'Loss Reasons',
  strategic_fit_score: 'Strategic Fit Score',
};

// Ordered list for display
const METRIC_ORDER = [
  'company_type',
  'headquarters',
  'founded_year',
  'revenue',
  'revenue_growth',
  'net_income',
  'market_cap',
  'employee_count',
  'product_count',
  'notable_customers',
  'kb_completeness',
  'competitive_position',
  'target_markets',
  'key_differentiators',
  'win_reasons',
  'loss_reasons',
];

const COMPANY_TYPE_LABELS: Record<string, string> = {
  public: 'Public',
  pe_backed: 'PE-Backed',
  private: 'Private',
  subsidiary: 'Subsidiary',
};

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const colors: Record<string, string> = {
    high: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`ml-1 inline-flex items-center rounded px-1 py-0.5 text-[10px] font-medium uppercase ${colors[confidence] || 'bg-gray-100 text-gray-600'}`}
    >
      {confidence}
    </span>
  );
}

function KBProgressBar({ value }: { value: number }) {
  const percentage = Math.round(value * 100);
  const barColor =
    percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 rounded-full bg-gray-200">
        <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-sm">{percentage}%</span>
    </div>
  );
}

function MetricCell({
  metric,
  metricKey,
  totalColumns,
}: {
  metric: CompanyComparisonMetric | undefined;
  metricKey: string;
  totalColumns: number;
}) {
  if (!metric || metric.value === null || metric.value === undefined) {
    return <span className="text-gray-400">N/A</span>;
  }

  // Rank-based highlighting (AC-007)
  let bgClass = '';
  if (metric.rank !== null && metric.rank !== undefined) {
    if (metric.rank === 1 && totalColumns > 1) {
      bgClass = 'bg-green-50';
    } else if (metric.rank === totalColumns && totalColumns > 1) {
      bgClass = 'bg-amber-50';
    }
  }

  // KB completeness: show progress bar
  if (metricKey === 'kb_completeness' && typeof metric.value === 'number') {
    return (
      <div className={bgClass ? `rounded px-1 ${bgClass}` : ''}>
        <KBProgressBar value={metric.value} />
      </div>
    );
  }

  // List metrics: show as pills
  if (metricKey === 'target_markets' && Array.isArray(metric.value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {(metric.value as string[]).map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
          >
            {item}
          </span>
        ))}
      </div>
    );
  }

  // Bullet list metrics
  if (
    ['key_differentiators', 'win_reasons', 'loss_reasons'].includes(metricKey) &&
    Array.isArray(metric.value)
  ) {
    return (
      <ul className="list-disc list-inside text-sm space-y-0.5">
        {(metric.value as string[]).map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  // Company type badge
  if (metricKey === 'company_type') {
    const label = COMPANY_TYPE_LABELS[metric.value as string] || (metric.value as string);
    return (
      <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
        {label}
      </span>
    );
  }

  // Default: formatted value + confidence + estimated
  return (
    <div className={bgClass ? `rounded px-1 ${bgClass}` : ''}>
      <span className="text-sm">{metric.formatted_value || String(metric.value)}</span>
      {metric.confidence && <ConfidenceBadge confidence={metric.confidence} />}
      {metric.is_estimated && <span className="ml-1 text-[10px] text-gray-400">(est)</span>}
    </div>
  );
}

export function ComparisonTable({ columns, availableMetrics }: ComparisonTableProps) {
  const availableSet = new Set(availableMetrics);
  const displayMetrics = METRIC_ORDER.filter((key) => availableSet.has(key));

  return (
    <div className="overflow-x-auto" data-testid="comparison-table">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-40">
              Metric
            </th>
            {columns.map((col) => (
              <th key={col.company_id} className="px-4 py-3 text-left min-w-[200px]">
                <div className="font-semibold text-gray-900">{col.company_name}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  {col.ticker && (
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                      {col.ticker}
                    </span>
                  )}
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    {COMPANY_TYPE_LABELS[col.company_type] || col.company_type}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {displayMetrics.map((metricKey) => (
            <tr key={metricKey} className="hover:bg-gray-50">
              <td className="sticky left-0 z-10 bg-white px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                {METRIC_LABELS[metricKey] || metricKey}
              </td>
              {columns.map((col) => (
                <td key={col.company_id} className="px-4 py-3">
                  <MetricCell
                    metric={col.metrics[metricKey]}
                    metricKey={metricKey}
                    totalColumns={columns.length}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
